import type {
  AdventureContent,
  AdventureRound,
  GameAction,
  GameState,
  GameSummary,
  PlacedThought,
  SelectedThoughtRecord,
} from "../types/adventure";
import {
  buttonsAreBlocked,
  countSafeZoneSlots,
  placeThoughts,
  placementSeedFromId,
} from "./thought-positions";

export const FINAL_ROUND_SECONDS = 20;
/** @deprecated Use FINAL_ROUND_SECONDS */
export const ROUND5_SECONDS = FINAL_ROUND_SECONDS;
export const REQUIRED_HELPFUL = 5;

export const REVEAL_PAUSE_MS = 750;
export const OUTCOME_MS = 4400;

export const INITIAL_STATE: GameState = {
  phase: "select",
  stage: "reveal",
  adventureId: null,
  roundIndex: 0,
  thoughts: [],
  choices: [],
  selectedHelpfulIds: [],
  revealedCount: 0,
  lastOutcome: null,
  lastBest: false,
  timeLeft: 0,
  timerExpired: false,
  buttonsBlocked: false,
};

function appendRoundThoughts(
  existing: PlacedThought[],
  round: AdventureRound,
  adventureId: string
): PlacedThought[] {
  const newThoughts = placeThoughts(
    round.thoughts.map((t) => ({
      id: t.id,
      system: t.system,
      helpful: t.helpful,
      text: t.text,
      round: round.n,
      overlay: t.overlay,
    })),
    countSafeZoneSlots(existing),
    placementSeedFromId(adventureId),
    existing
  );
  return [...existing, ...newThoughts];
}

function getRound(adventure: AdventureContent, index: number): AdventureRound | null {
  return adventure.rounds[index] ?? null;
}

export function isFinalRound(adventure: AdventureContent, roundIndex: number): boolean {
  return getRound(adventure, roundIndex)?.n === 5;
}

function buildThoughtsThroughRound(
  adventure: AdventureContent,
  roundIndex: number
): PlacedThought[] {
  let thoughts: PlacedThought[] = [];
  for (let i = 0; i <= roundIndex; i++) {
    const round = getRound(adventure, i);
    if (!round) break;
    thoughts = appendRoundThoughts(thoughts, round, adventure.id);
  }
  return thoughts;
}

function buildAllThoughts(adventure: AdventureContent): PlacedThought[] {
  const lastIndex = adventure.rounds.length - 1;
  return lastIndex >= 0 ? buildThoughtsThroughRound(adventure, lastIndex) : [];
}

function pickDefaultHelpfulIds(thoughts: PlacedThought[]): string[] {
  return thoughts
    .filter((t) => t.helpful)
    .slice(0, REQUIRED_HELPFUL)
    .map((t) => t.thoughtId);
}

function debugPostGameBase(adventure: AdventureContent) {
  const thoughts = buildAllThoughts(adventure);
  const lastRoundIndex = Math.max(adventure.rounds.length - 1, 0);
  return {
    roundIndex: lastRoundIndex,
    thoughts,
    choices: [],
    revealedCount: thoughts.length,
    lastOutcome: null,
    lastBest: false,
    stage: "challenge" as const,
  };
}

export function createSummary(
  adventure: AdventureContent,
  state: GameState
): GameSummary {
  const selectedHelpful: SelectedThoughtRecord[] = state.selectedHelpfulIds
    .map((id) => state.thoughts.find((t) => t.thoughtId === id))
    .filter((t): t is PlacedThought => !!t)
    .map((t) => ({
      thoughtId: t.thoughtId,
      text: t.text,
      system: t.system,
      helpful: t.helpful,
    }));

  return {
    adventureId: adventure.id,
    adventureLabel: adventure.label,
    choices: state.choices,
    selectedHelpful,
  };
}

export function gameReducer(
  state: GameState,
  action: GameAction,
  adventure: AdventureContent | null
): GameState {
  switch (action.type) {
    case "SELECT_ADVENTURE":
      return {
        ...INITIAL_STATE,
        phase: "intro",
        adventureId: action.adventureId,
      };

    case "START_GAME": {
      if (!adventure) return state;
      const round = getRound(adventure, 0);
      if (!round) return state;
      const thoughts = appendRoundThoughts([], round, adventure.id);
      return {
        ...state,
        phase: "playing",
        stage: "reveal",
        roundIndex: 0,
        thoughts,
        choices: [],
        selectedHelpfulIds: [],
        revealedCount: 0,
        lastOutcome: null,
        lastBest: false,
        timeLeft: 0,
        timerExpired: false,
        buttonsBlocked: false,
      };
    }

    case "TICK": {
      if (state.phase !== "playing" || !adventure) return state;
      if (!isFinalRound(adventure, state.roundIndex)) return state;
      if (state.stage !== "challenge") return state;
      const next = state.timeLeft - 1;
      if (next <= 0) {
        return {
          ...state,
          phase: "crash",
          timeLeft: 0,
          timerExpired: true,
          buttonsBlocked: true,
        };
      }
      return { ...state, timeLeft: next };
    }

    case "REVEAL_NEXT": {
      if (state.phase !== "playing" || state.stage !== "reveal") return state;
      const nextCount = Math.min(state.revealedCount + 1, state.thoughts.length);
      return { ...state, revealedCount: nextCount };
    }

    case "BEGIN_CHALLENGE": {
      if (state.phase !== "playing" || state.stage !== "reveal") return state;
      const blocked = buttonsAreBlocked(state.thoughts, "playing");
      const startFinalTimer =
        !!adventure && isFinalRound(adventure, state.roundIndex);
      return {
        ...state,
        stage: "challenge",
        buttonsBlocked: blocked,
        ...(startFinalTimer ? { timeLeft: FINAL_ROUND_SECONDS } : {}),
      };
    }

    case "CHOOSE_OPTION": {
      if (
        !adventure ||
        state.phase !== "playing" ||
        state.stage !== "challenge" ||
        state.buttonsBlocked
      ) {
        return state;
      }
      const choice = {
        round: adventure.rounds[state.roundIndex].n,
        optionId: action.optionId,
        best: action.best,
        outcome: action.outcome,
      };
      return {
        ...state,
        stage: "outcome",
        choices: [...state.choices, choice],
        lastOutcome: action.outcome,
        lastBest: action.best,
      };
    }

    case "ADVANCE_ROUND": {
      if (!adventure || state.phase !== "playing" || state.stage !== "outcome") {
        return state;
      }
      const nextIndex = state.roundIndex + 1;
      if (nextIndex >= adventure.rounds.length) {
        return { ...state, phase: "summary" };
      }
      const nextRound = getRound(adventure, nextIndex)!;
      const thoughts = appendRoundThoughts(state.thoughts, nextRound, adventure.id);
      return {
        ...state,
        stage: "reveal",
        roundIndex: nextIndex,
        thoughts,
        revealedCount: state.revealedCount,
        lastOutcome: null,
        buttonsBlocked: false,
        timeLeft: 0,
      };
    }

    case "CRASH_CONTINUE":
      return { ...state, phase: "lesson" };

    case "LESSON_CONTINUE":
      return {
        ...state,
        phase: "defuse",
        timerExpired: false,
        buttonsBlocked: true,
        selectedHelpfulIds: [],
      };

    case "TOGGLE_HELPFUL_SELECTION": {
      if (state.phase !== "defuse") return state;

      const thought = state.thoughts.find((t) => t.thoughtId === action.thoughtId);
      if (!thought || !thought.helpful) return state;

      const isSelected = state.selectedHelpfulIds.includes(action.thoughtId);
      let selectedHelpfulIds: string[];

      if (isSelected) {
        selectedHelpfulIds = state.selectedHelpfulIds.filter(
          (id) => id !== action.thoughtId
        );
      } else if (state.selectedHelpfulIds.length >= REQUIRED_HELPFUL) {
        return state;
      } else {
        selectedHelpfulIds = [...state.selectedHelpfulIds, action.thoughtId];
      }

      if (selectedHelpfulIds.length === REQUIRED_HELPFUL) {
        return {
          ...state,
          phase: "choose",
          selectedHelpfulIds,
          buttonsBlocked: false,
        };
      }

      return { ...state, selectedHelpfulIds };
    }

    case "FINISH_CHOICE": {
      const choice = {
        round: 5,
        optionId: action.optionId,
        best: action.best,
        outcome: action.outcome,
      };
      return {
        ...state,
        phase: "summary",
        choices: [...state.choices, choice],
        lastOutcome: action.outcome,
        lastBest: action.best,
      };
    }

    case "BACK_TO_SELECT":
      return { ...INITIAL_STATE };

    case "DEBUG_JUMP_ROUND": {
      if (!adventure) return state;
      const roundIndex = action.roundIndex;
      if (roundIndex < 0 || roundIndex >= adventure.rounds.length) return state;

      const round = getRound(adventure, roundIndex)!;
      const thoughts = buildThoughtsThroughRound(adventure, roundIndex);
      const timeLeft = round.n === 5 ? FINAL_ROUND_SECONDS : 0;

      return {
        ...state,
        phase: "playing",
        stage: "challenge",
        roundIndex,
        thoughts,
        choices: [],
        selectedHelpfulIds: [],
        revealedCount: thoughts.length,
        lastOutcome: null,
        lastBest: false,
        timeLeft,
        timerExpired: false,
        buttonsBlocked: buttonsAreBlocked(thoughts, "playing"),
      };
    }

    case "DEBUG_JUMP_PHASE": {
      if (!adventure) return state;
      const base = debugPostGameBase(adventure);

      switch (action.phase) {
        case "crash":
          return {
            ...state,
            ...base,
            phase: "crash",
            timeLeft: 0,
            timerExpired: true,
            buttonsBlocked: true,
            selectedHelpfulIds: [],
          };
        case "lesson":
          return {
            ...state,
            ...base,
            phase: "lesson",
            timeLeft: 0,
            timerExpired: true,
            buttonsBlocked: true,
            selectedHelpfulIds: [],
          };
        case "defuse":
          return {
            ...state,
            ...base,
            phase: "defuse",
            timeLeft: 0,
            timerExpired: false,
            buttonsBlocked: true,
            selectedHelpfulIds: [],
          };
        case "choose": {
          const selectedHelpfulIds = pickDefaultHelpfulIds(base.thoughts);
          return {
            ...state,
            ...base,
            phase: "choose",
            timeLeft: 0,
            timerExpired: false,
            buttonsBlocked: false,
            selectedHelpfulIds,
          };
        }
      }
    }

    default:
      return state;
  }
}
